import React, { useEffect, useState } from 'react';
import Modal from 'react-responsive-modal';
import ApiService from '../services/api.service';
import AuthService from '../services/auth.service';

const OTHER_REASON = 'OTRO';

// Motivos más comunes de anulación. "Otro" pide que el admin escriba el motivo.
const CANCEL_REASONS = [
  'Monto ingresado por error',
  'Pago duplicado',
  'Pago registrado en la orden equivocada',
  'Voucher inválido o no corresponde al cliente',
  'Pago no recibido en la cuenta',
  'Pedido cancelado o devuelto',
];

const MAX_REASON_LENGTH = 200;

const INPUT_STYLE = { minHeight: 48, fontSize: 16 };
const BTN_STYLE   = { minHeight: 48, fontSize: 16 };

// Modal de anulación de un pago en dos pasos: elegir el motivo y confirmar.
const CancelChargeModal = ({ charge, onClose, onCancelled }) => {
  const [step, setStep]               = useState('form');
  const [reasonOption, setReasonOption] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    setStep('form');
    setReasonOption('');
    setOtherReason('');
    setSaving(false);
    setError('');
  }, [charge]);

  if (!charge) return null;

  const reason  = reasonOption === OTHER_REASON ? otherReason.trim() : reasonOption;
  const isValid = !!reason;
  const amount  = parseFloat(charge.value || 0).toFixed(2);
  const client  = charge.Purchase && charge.Purchase.User
    ? `${charge.Purchase.User.name || ''} ${charge.Purchase.User.last_name || ''}`.trim()
    : '';
  // Un pago que no es asignación puede haber generado asignaciones en otras órdenes.
  const mayHaveAllocations = charge.source !== 'ADMIN-ALLOCATION';

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      const user = AuthService.getCurrentUser();
      const res  = await ApiService.cancelPurchaseCharge(user.token, charge.id, reason);
      if (res && res.status === 'success') {
        onCancelled(res.data && res.data.cancelled ? res.data.cancelled : 1);
      } else {
        setError((res && res.message) || 'No se pudo anular el pago.');
        setSaving(false);
      }
    } catch (e) {
      setError('No se pudo anular el pago. Intente nuevamente.');
      setSaving(false);
    }
  };

  const summary = (
    <div className="card mb-3" style={{ background: '#f8f9fa' }}>
      <div className="card-body py-2 px-3" style={{ fontSize: 14 }}>
        <div className="d-flex justify-content-between">
          <span className="text-muted">Pago</span><strong>#{charge.id}</strong>
        </div>
        <div className="d-flex justify-content-between">
          <span className="text-muted">Orden</span><strong>#{charge.id_purchase}</strong>
        </div>
        {client && (
          <div className="d-flex justify-content-between">
            <span className="text-muted">Cliente</span><strong className="text-right">{client}</strong>
          </div>
        )}
        <div className="d-flex justify-content-between">
          <span className="text-muted">Monto</span><strong>S/ {amount}</strong>
        </div>
      </div>
    </div>
  );

  return (
    <Modal open={!!charge} onClose={handleClose} center>
      <div style={{ width: '100%', maxWidth: 440, minWidth: 280 }}>
        <h5 className="mb-3">{step === 'form' ? 'Anular pago' : 'Confirmar anulación'}</h5>

        {summary}

        {step === 'form' && (
          <>
            <div className="form-group">
              <label className="mb-1" style={{ fontSize: 14 }}>Motivo de la anulación</label>
              <select
                className="form-control"
                style={INPUT_STYLE}
                value={reasonOption}
                onChange={(e) => setReasonOption(e.target.value)}
                autoFocus
              >
                <option value="">Seleccione un motivo...</option>
                {CANCEL_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
                <option value={OTHER_REASON}>Otro</option>
              </select>
            </div>

            {reasonOption === OTHER_REASON && (
              <div className="form-group">
                <label className="mb-1" style={{ fontSize: 14 }}>Indique el motivo</label>
                <textarea
                  className="form-control"
                  style={{ fontSize: 16 }}
                  rows={3}
                  maxLength={MAX_REASON_LENGTH}
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Describa el motivo de la anulación"
                  autoFocus
                />
                <small className="text-muted">{otherReason.length}/{MAX_REASON_LENGTH}</small>
              </div>
            )}

            <div className="row mt-3">
              <div className="col-6 pr-1">
                <button type="button" className="btn btn-light btn-block" style={BTN_STYLE} onClick={handleClose}>
                  Cancelar
                </button>
              </div>
              <div className="col-6 pl-1">
                <button
                  type="button"
                  className="btn btn-danger btn-block"
                  style={BTN_STYLE}
                  disabled={!isValid}
                  onClick={() => setStep('confirm')}
                >
                  Continuar
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="alert alert-warning" style={{ fontSize: 14 }}>
              <div className="mb-1"><strong>Motivo:</strong> {reason}</div>
              <div>
                El pago dejará de contar como cobrado y, si la orden queda con saldo, volverá a
                <strong> Por cobrar</strong>. Esta acción no se puede deshacer.
              </div>
              {mayHaveAllocations && (
                <div className="mt-1">
                  Si este pago generó asignaciones en otras órdenes, también se anularán.
                </div>
              )}
            </div>

            {error && <div className="alert alert-danger" style={{ fontSize: 14 }}>{error}</div>}

            <div className="row mt-3">
              <div className="col-6 pr-1">
                <button
                  type="button"
                  className="btn btn-light btn-block"
                  style={BTN_STYLE}
                  disabled={saving}
                  onClick={() => { setStep('form'); setError(''); }}
                >
                  Volver
                </button>
              </div>
              <div className="col-6 pl-1">
                <button
                  type="button"
                  className="btn btn-danger btn-block"
                  style={BTN_STYLE}
                  disabled={saving}
                  onClick={handleConfirm}
                >
                  {saving && <span className="spinner-border spinner-border-sm mr-1" />}
                  Sí, anular
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CancelChargeModal;
